import { Controller } from "@hotwired/stimulus"

// A Community room in San Francisco (Community::Worlds): mounts the 3D city from the community_sf
// bundle into this element and wires it to the room, the same way community-square does for the
// Square - no socket of its own. community-chat relays the cable's roster / join / leave / move /
// said frames as "community-square:frame" window events and sends our move and ping frames for us,
// so the page still holds one subscription.
//
// The bundle is a separate <script type="module"> (it is large), so it may arrive before or after
// this controller connects: either window.JaneWorlds.sf is already there, or "jane-worlds:ready"
// says when it is. Leaving the page (Turbo) disposes the city and frees its WebGL context.
export default class extends Controller {
    static values = { config: Object }

    connect() {
        // The cable says hello and sends the roster as soon as the page subscribes, seconds before
        // the city has loaded: hold every frame until it has, then replay them in order.
        this.pending = []
        this.onFrame = (e) => {
            if (this.city) this.city.frame(e.detail)
            else if (this.pending.length < 5000) this.pending.push(e.detail)
        }
        window.addEventListener("community-square:frame", this.onFrame)
        this.onReady = () => this.#boot()
        window.addEventListener("jane-worlds:ready", this.onReady)
        this.#boot()
    }

    disconnect() {
        window.removeEventListener("community-square:frame", this.onFrame)
        window.removeEventListener("jane-worlds:ready", this.onReady)
        this.gone = true
        this.city?.dispose()
        this.city = null
    }

    async #boot() {
        const world = window.JaneWorlds?.sf
        if (!world || this.booting || this.gone) return
        this.booting = true
        const config = this.configValue
        try {
            const city = await world.bootCity(this.element, {
                dataPath: config.data_path,
                me: config.me,
                hearing: config.hearing,
                iceServers: config.ice_servers,
                photoreal: config.photoreal,
                streetTrees: config.street_trees,
                places: config.places,
                homePath: config.home_path,
                homeChoices: config.home_choices,
                blocked: config.blocked,
                safety: config.safety,
                send: (action, payload) => window.dispatchEvent(new CustomEvent("community-square:send", { detail: { action, payload } })),
                position: (x, y) => window.dispatchEvent(new CustomEvent("community-square:position", { detail: { x, y } })),
                onTalk: () => this.#talk()
            })
            if (this.gone) { city.dispose(); return }
            this.city = city
            this.pending.splice(0).forEach((frame) => city.frame(frame))
            this.element.classList.add("is-ready")
        } catch (e) {
            if (this.gone) return
            console.error(e)
            const step = this.element.querySelector(".sfw-loader-step")
            if (step) step.textContent = "San Francisco could not load here. Try the Square instead."
        }
    }

    // Enter to talk: over to the chat composer, the same as on the Square.
    #talk() {
        window.dispatchEvent(new CustomEvent("community-chat:open"))
        document.querySelector("[data-community-chat-target='input']")?.focus()
    }
}
